import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import SharedBadge from '@/global/Badge/Badge';
import SharedButton from '@/global/Button/Button';
import { trackEvent } from '@/utils/analytics';
import {
  getNumericScoreFromSearchItem,
  getSearchItemBadgeLabel,
} from '@/modules/search/searchGrid/utils/searchResultsHelpers';
import '../SearchResults.css';
import './SearchItemsStrip.css';

type SearchItemsStripProps = {
  searchItems: any[];
  entityPlural: string;
  onOpenItemInfo?: (item: any) => void;
  onRemoveItem: (index: number) => void;
  onClearAll?: () => void;
};

export function SearchItemsStrip({
  searchItems,
  entityPlural,
  onOpenItemInfo,
  onRemoveItem,
  onClearAll,
}: SearchItemsStripProps) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  const indexedItems = (searchItems ?? []).map((item, originalIndex) => ({
    item,
    originalIndex,
    ...getSearchItemBadgeLabel(item),
    score: getNumericScoreFromSearchItem(item),
  }));

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const visibleItems = indexedItems.filter(
    entry => !normalizedSearchTerm || entry.label.toLowerCase().includes(normalizedSearchTerm),
  );

  useEffect(() => {
    if (!isPanelOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsPanelOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsPanelOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isPanelOpen]);

  useEffect(() => {
    if (!indexedItems.length) {
      setIsPanelOpen(false);
      setSearchTerm('');
    }
  }, [indexedItems.length]);

  if (!indexedItems.length) return null;

  const PREVIEW_LIMIT = 3;
  const previewItems = indexedItems.slice(0, PREVIEW_LIMIT);
  const remainingCount = indexedItems.length - previewItems.length;
  const searchLabel = indexedItems.length === 1 ? 'search' : 'searches';

  const renderChip = ({
    item,
    originalIndex,
    label,
    score,
  }: {
    item: any;
    originalIndex: number;
    label: string;
    score: number | null;
  }) => {
    const canOpenInfo = !!onOpenItemInfo;

    return (
      <div
        key={`${String(item?.id ?? label)}-${originalIndex}`}
        className="search-items-strip-chip"
        onClick={() => {
          if (!canOpenInfo) return;
          trackEvent({
            name: 'open_item_info',
            buttonId: 'search_items_strip_open_info',
            label: 'Open search item info',
            page: 'search_results',
            section: 'search_items_strip',
            feature: 'search_items',
            eventGroup: 'search',
            eventCategory: 'search',
          });
          onOpenItemInfo(item);
        }}
        onKeyDown={event => {
          if (!canOpenInfo) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpenItemInfo(item);
          }
        }}
        role={canOpenInfo ? 'button' : undefined}
        tabIndex={canOpenInfo ? 0 : undefined}
        aria-label={canOpenInfo ? `Open info for ${label}` : undefined}
      >
        <SharedBadge
          text={
            <span className="search-items-tag-content flex min-w-0 max-w-[18rem] items-center gap-1.5">
              <span className="search-items-tag-label min-w-0 flex-1 truncate" title={label}>
                {label}
              </span>
              {score != null && (
                <span className="shrink-0 rounded bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-slate-700">
                  {score}
                </span>
              )}
              <span aria-hidden="true" className="search-items-tag-divider" />
              <button
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  trackEvent({
                    name: 'remove_search_item',
                    buttonId: 'search_items_strip_remove_item',
                    label: 'Remove search item from strip',
                    page: 'search_results',
                    section: 'search_items_strip',
                    feature: 'search_items',
                    eventGroup: 'search',
                    eventCategory: 'search',
                  });
                  if (indexedItems.length === 1 && onClearAll) {
                    onClearAll();
                  } else {
                    onRemoveItem(originalIndex);
                  }
                }}
                className="search-items-tag-close shrink-0 border-0 bg-transparent pl-0.5"
                aria-label={`Remove ${label}`}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          }
          variant="primaryOutline"
          className="search-items-tag flex h-auto min-h-7 max-w-full items-center justify-start overflow-hidden text-left"
          style={{ marginBottom: 0 }}
        />
      </div>
    );
  };

  return (
    <div className="search-items-strip mb-3 mt-2 mr-8" data-testid="search-items-strip" ref={panelRef}>
      <div className="search-items-summary-row">
        <SharedButton
          id="search-items-summary-trigger"
          aria-expanded={isPanelOpen}
          aria-controls="search-items-panel"
          variant="ghost"
          size="xs"
          onClick={() => {
            trackEvent({
              name: 'dropdown_toggle',
              buttonId: 'search_items_summary_toggle',
              label: 'Toggle search items panel',
              page: 'search_results',
              section: 'search_items_strip',
              feature: 'search_items',
              eventGroup: 'search',
              eventCategory: 'search',
            });
            setIsPanelOpen(open => !open);
          }}
          className="search-items-summary-trigger mb-0"
          iconLeft={<MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-slate-500" />}
          iconRight={
            <ChevronDownIcon
              className="h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200"
              style={{ transform: isPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
            />
          }
        >
          <span className="search-items-summary-content">
            <span className="search-items-summary-count">
              {indexedItems.length} {searchLabel}
            </span>
            <span className="search-items-summary-divider" aria-hidden="true">
              ·
            </span>
            <span className="search-items-summary-preview">
              {previewItems.map((entry, index) => (
                <span
                  key={`${entry.label}-${entry.originalIndex}`}
                  className="search-items-summary-preview-item"
                >
                  {index > 0 && <span className="search-items-summary-preview-sep">·</span>}
                  <span className="search-items-summary-preview-label" title={entry.label}>
                    {entry.label}
                  </span>
                </span>
              ))}
              {remainingCount > 0 && (
                <span className="search-items-summary-preview-more">+{remainingCount}</span>
              )}
            </span>
          </span>
        </SharedButton>
      </div>

      {isPanelOpen && (
        <div
          id="search-items-panel"
          role="region"
          aria-labelledby="search-items-summary-trigger"
          className="search-items-panel"
        >
          <div className="search-items-panel-header">
            <div className="relative min-w-0 flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 h-4 w-4 -translate-y-1/2 text-slate-400 mt-2.5" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search applied items..."
                className="filter-input search-items-search-input"
              />
            </div>
            {onClearAll && (
              <SharedButton
                type="button"
                variant="secondaryText"
                size="xs"
                className="mb-0 shrink-0 mt-0"
                onClick={() => {
                  trackEvent({
                    name: 'clear_all_search_items',
                    buttonId: 'search_items_strip_clear_all',
                    label: 'Clear all search items',
                    page: 'search_results',
                    section: 'search_items_strip',
                    feature: 'search_items',
                    eventGroup: 'search',
                    eventCategory: 'search',
                  });
                  onClearAll();
                }}
              >
                Clear all
              </SharedButton>
            )}
          </div>

          <div className="search-items-panel-body">
            {visibleItems.length === 0 ? (
              <p className="mb-0 px-1 py-2 text-sm text-slate-500">No searches match your search.</p>
            ) : (
              <div className="search-items-chip-list">{visibleItems.map(renderChip)}</div>
            )}
          </div>
        </div>
      )}

      <span className="sr-only">
        {indexedItems.length} {entityPlural} in current search
      </span>
    </div>
  );
}
